import { ArrowContainer, Popover } from 'react-tiny-popover';
import React, { useState } from 'react';
import { HiDotsVertical } from 'react-icons/hi';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { CachePolicies, useFetch } from 'use-http';
import Switch from 'react-ios-switch';
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params';
import useAsyncEffect from 'use-async-effect';
import { HSpace, VSpace } from '@/components/spacing';
import { Container } from '@/features/details/sessions-type-chip';
import { FeatureBit } from '@/webapi/use-auth-api';
import { ExpVersion } from '@/webapi/use-analytics-api';
import { countDays } from '@/features/details/date-pick/quick-range-select';
import { routes } from '@/webapi/routes';
import { Spinner } from '@/Spinner';
import { longPoll } from '@/utils/sync';
import { Tooltip } from '@/components/tooltip';
import { isSmoothRevenueAvailable } from '@/features/details/summary/context';
import { Experience } from '@/webapi/use-experience-api';
import { CIToolTip } from '@/features/details/summary/confidence-interval';
import { useFeatureBit } from '@/features/account-context';
import { useStoreSettingsApi } from '@/webapi/use-store-settings-api';

function VariationsList({
  onClick,
  versionSelectOpen,
  versions,
  selectedVersion,
  currentVersion,
  selectVersion,
}: {
  onClick: (e) => void;
  versionSelectOpen: boolean;
  versions?: Array<ExpVersion>;
  selectedVersion?: number;
  currentVersion?: number;
  selectVersion?: (value: ((prevState: number) => number) | number) => void;
}) {
  const isSingleVersion = versions?.length === 1;
  return (
    <VersionSelector>
      <Row>
        <Column>
          {versions
            ?.filter(
              (v) =>
                isAfterMinDate(new Date(v.startDate)) &&
                (versionSelectOpen || selectedVersion === v.version),
            )
            ?.map((v) => (
              <Version
                isSelected={selectedVersion === v.version}
                key={v.version}
                onClick={(e) => {
                  e.stopPropagation();
                  selectVersion(v.version);
                }}
              >
                Version {v.version} -{` `}
                {v?.version === currentVersion ? `current ` : ``} (
                {countDays(v)} Days)
              </Version>
            ))}
        </Column>
        {!isSingleVersion && (
          <Toggle onClick={onClick}>
            {versionSelectOpen ? (
              <IoIosArrowUp size={15} />
            ) : (
              <IoIosArrowDown size={15} />
            )}
          </Toggle>
        )}
      </Row>
    </VersionSelector>
  );
}

function ExportType({
  selected,
  selectExport,
}: {
  selected?: string;
  selectExport?: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <VersionSelector>
      <Row>
        <Column>
          {[OrderNumbers, ExperienceData, BestSellingItems]
            .filter((x) => isOpen || selected === x)
            .map((v) => (
              <Version
                isSelected={selected === v}
                key={v}
                onClick={(e) => {
                  e.stopPropagation();
                  selectExport(v);
                  setIsOpen(false);
                }}
              >
                {v}
              </Version>
            ))}
        </Column>
        <Toggle
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isOpen ? <IoIosArrowUp size={15} /> : <IoIosArrowDown size={15} />}
        </Toggle>
      </Row>
    </VersionSelector>
  );
}

const OrderNumbers = `Order Numbers`;
const ExperienceData = `Experience Data`;
const BestSellingItems = `Best Selling Items`;

function OutlierTooltip() {
  const text = ` metric's`;
  return (
    <Tooltip
      zIndex={0}
      positions={[`bottom`]}
      title="Exclude Outliers"
      text={
        <p>
          An order is marked as an outlier if its total price is larger than the
          moving 7-day arithmetic average + 3 times the 7-day standard
          deviation.
          <br />
          <br />
          <code>total {`>`} AVG + 3 * STDDEV </code>
          <br />
          <br />
          <code>THRESHOLD = AVG + 3 * STDDEV </code>
          <br />
          <br />
          These extreme values are designated as outliers. Next, Visually
          replaces these outliers with the
          {text} 7-day harmonic mean value. This process is known as outlier
          smoothing. Visually recalculates the daily exclusion threshold each
          day for every experience.
        </p>
      }
    />
  );
}

const hasABTest = (experience: Experience) =>
  experience?.versions?.find((v) =>
    Object.values(v?.variantChances || {}).find((v) => v !== 100),
  );

function ConfidenceIntervalToggle({
  disabled,
  onChange,
  shouldShowCI,
}: {
  disabled: boolean;
  shouldShowCI: boolean;
  onChange: () => Promise<void>;
}) {
  const ciEnabled = useFeatureBit(FeatureBit.CONFIDENCE_INTERVALS);
  if (!ciEnabled) {
    return null;
  }
  return (
    <>
      <FlexRow>
        <Col>
          <FlexRow>
            <SubTitle>Confidence Interval</SubTitle>
            <CIToolTip />
          </FlexRow>
          <VSpace />
          <Description>
            Displaying the confidence interval for the main A/B test goal
          </Description>
        </Col>
        <SmallSwitch
          disabled={disabled}
          offColor="rgba(120,120,128,0.16)"
          checked={shouldShowCI}
          onChange={onChange}
        />
      </FlexRow>
      <VSpace />
      <Separator />
      <VSpace />
    </>
  );
}

const useConfidenceIntervals = (
  setLoading: (value: ((prevState: boolean) => boolean) | boolean) => void,
) => {
  const [shouldShowCI, setShouldShowCI] = useQueryParam<boolean>(
    `showCI`,
    withDefault(BooleanParam, false),
  );

  const { setUserSettings, getUserSettings } = useStoreSettingsApi();

  useAsyncEffect(async () => {
    setLoading(true);
    try {
      const userSettings = await getUserSettings();
      setShouldShowCI(userSettings?.isConfidenceIntervalsEnabled === `YES`);
    } finally {
      setLoading(false);
    }
  }, []);

  const onConfidenceIntervalToggle = async () => {
    setLoading(true);
    try {
      setUserSettings({
        isConfidenceIntervalsEnabled: shouldShowCI ? `NO` : `YES`,
      });
      setShouldShowCI(!shouldShowCI);
    } finally {
      setLoading(false);
    }
  };
  return { shouldShowCI, onConfidenceIntervalToggle };
};

export function DownloadOrdersChip({
  metricsStart,
  versions,
  expId,
  experience,
}: {
  metricsStart?: string;
  expId?: string;
  versions?: Array<ExpVersion>;
  experience?: Experience;
}) {
  const { post, put } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
    },
  }));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [versionSelectOpen, setVersionSelectOpen] = useState(false);
  const currentVersion = getCurrentVersion(versions);
  const [selectedVersion, selectVersion] = useState(currentVersion);

  const notAvailable = isNotOrderDownloadAvailable(metricsStart);

  const onMetricsExport = async (e) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setLoading(true);
    const downloadLink = routes.exportStats(expId, `${selectedVersion}`);
    await downloadFile(downloadLink, `stats`, expId, setLoading);
  };
  const [exportType, setExportType] = useState(OrderNumbers);
  const onOrdersExport = async (e) => {
    if (exportType === BestSellingItems) {
      await onBestSellertsExport(e);
      return;
    }
    if (exportType === ExperienceData) {
      await onMetricsExport(e);
      return;
    }
    e.stopPropagation();
    if (loading) {
      return;
    }
    setLoading(true);
    const action = async () => {
      const result = await post(routes.exportOrders, {
        longPol: true,
        experienceId: expId,
        version: selectedVersion,
      });
      return result.downloadLink;
    };
    const downloadLink = await longPoll<string>(action, 1000);
    await downloadFile(downloadLink, `orders`, expId, setLoading);
  };

  const onBestSellertsExport = async (e) => {
    e.stopPropagation();
    if (loading) {
      return;
    }
    setLoading(true);
    const action = async () =>
      (
        await post(routes.exportOrders, {
          experienceId: expId,
          longPol: true,
          byLineItems: true,
          version: selectedVersion,
        })
      ).downloadLink;
    const downloadLink = await longPoll<string>(action, 1000);
    await downloadFile(downloadLink, `best_sellers`, expId, setLoading);
  };

  const outliersToggleEnabled = useFeatureBit(FeatureBit.METRICS_OUTLIERS);
  const [outlierSmooth, setOutlierSmooth] = useQueryParam<boolean>(
    `outlier`,
    withDefault(BooleanParam, !!experience?.smoothRevenueEnabled),
  );
  const [otwForAll, setOtwForAll] = useQueryParam<boolean>(
    `otw4all`,
    withDefault(BooleanParam, !!experience?.otwForAllVariants),
  );
  const { shouldShowCI, onConfidenceIntervalToggle } =
    useConfidenceIntervals(setLoading);

  const showOutlierToggle =
    outliersToggleEnabled && isSmoothRevenueAvailable(metricsStart);
  const onSmoothRevenueToggle = async () => {
    const nextValue = !outlierSmooth;
    setLoading(true);
    try {
      await put(routes.saveExperienceSessionsType, {
        experienceId: expId,
        smoothRev: { enabled: nextValue ? `YES` : `NO` },
      });
      setOutlierSmooth(nextValue);
    } finally {
      setLoading(false);
    }
  };

  const onOtwForAllToggle = async () => {
    const nextValue = !otwForAll;
    setLoading(true);
    try {
      await put(routes.saveExperienceSessionsType, {
        experienceId: expId,
        otwForAll: { enabled: nextValue ? `YES` : `NO` },
      });
      setOtwForAll(!otwForAll);
    } finally {
      setLoading(false);
    }
  };

  if (notAvailable) {
    return null;
  }
  const isOtwForAllFlagEnabled = useFeatureBit(FeatureBit.OTW_ALL_GOALS);

  return (
    <Popover
      onClickOutside={() => setOpen(!open)}
      isOpen={open}
      positions={[`bottom`, `left`, `right`]}
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="white"
          arrowSize={20}
        >
          <PopoverContainer loading={loading}>
            {showOutlierToggle && (
              <>
                <FlexRow>
                  <Col>
                    <FlexRow>
                      <SubTitle>Exclude Outliers</SubTitle>
                      <OutlierTooltip />
                    </FlexRow>
                    <VSpace />
                    <Description>
                      Removes extreme data points to improve analysis accuracy
                    </Description>
                  </Col>
                  <SmallSwitch
                    disabled={loading}
                    offColor="rgba(120,120,128,0.16)"
                    checked={outlierSmooth}
                    onChange={onSmoothRevenueToggle}
                  />
                </FlexRow>
                <VSpace />
                <Separator />
                <VSpace />
              </>
            )}
            {!isOtwForAllFlagEnabled && hasABTest(experience) && (
              <>
                <FlexRow>
                  <Col>
                    <FlexRow>
                      <SubTitle>Show odds to win for all metrics</SubTitle>
                    </FlexRow>
                    <VSpace />
                    <Description>
                      By default, we only show odds to win for the main goal
                    </Description>
                  </Col>
                  <SmallSwitch
                    disabled={loading}
                    offColor="rgba(120,120,128,0.16)"
                    checked={otwForAll}
                    onChange={onOtwForAllToggle}
                  />
                </FlexRow>
                <VSpace />
                <Separator />
                <VSpace />
              </>
            )}
            <ConfidenceIntervalToggle
              disabled={loading}
              shouldShowCI={shouldShowCI}
              onChange={onConfidenceIntervalToggle}
            />
            <SubTitle>Export Reports</SubTitle>
            <VSpace />
            <TwoColumns>
              <Description>
                Export data for selected report in CSV format
              </Description>
              <DownloadBtn onClick={onOrdersExport}>
                {loading ? (
                  <Spinner color="white" mt="0px" size={20} />
                ) : (
                  <DownIcon />
                )}
              </DownloadBtn>
            </TwoColumns>
            <VSpace value={2} />
            <ExportType selected={exportType} selectExport={setExportType} />
            <VSpace />
            <VariationsList
              versions={versions}
              selectedVersion={selectedVersion}
              currentVersion={currentVersion}
              selectVersion={selectVersion}
              onClick={(e) => {
                e.stopPropagation();
                setVersionSelectOpen(!versionSelectOpen);
              }}
              versionSelectOpen={versionSelectOpen}
            />
            <VSpace />
          </PopoverContainer>
        </ArrowContainer>
      )}
    >
      <Container>
        <HSpace />
        <ChipWrap
          active={outlierSmooth || otwForAll}
          onClick={() => setOpen(!open)}
        >
          <HiDotsVertical />
        </ChipWrap>
      </Container>
    </Popover>
  );
}

const Toggle = styled.div`
  margin-top: 4px;
  align-self: start;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr;
  grid-gap: 1rem;
`;
const Column = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 310px;
  width: 160px;
  overflow-y: scroll;
`;
const Version = styled.div<{ isSelected: boolean }>`
  padding: 0.75rem;

  ${(p) => (p.isSelected ? `color: blue;` : ``)}
  &:hover {
    opacity: 0.5;
    cursor: pointer;
  }
`;
const VersionSelector = styled.div`
  padding: 1.5rem;
  background-color: #f7f7f7;
  color: #505050;

  font-family: Inter, serif;
  border-radius: 8px;
`;
const Separator = styled.span`
  background: #ebebeb;
  height: 1px;
  margin: 0.5rem 0;
  width: 100%;
`;

const PopoverContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background-color: white;
  padding: 2rem 3rem;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09), 0 1px 4px 0 rgba(7, 6, 6, 0.04);

  * {
    opacity: ${(p) => (p.loading ? 0.8 : 1)};
  }
`;
const TwoColumns = styled.div`
  width: 100%;
  font-family: Inter, serif;
  display: flex;
  justify-content: space-between;
`;
const SubTitle = styled.span`
  font-family: Inter, serif;
  font-size: 1.3rem;
  font-weight: 600;
  letter-spacing: -0.25px;
  color: #505050;
`;

const Description = styled.span`
  font-size: 1.1rem;
  margin-right: 1rem;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: -0.25px;
  color: #505050;
  align-self: center;
`;
const DownloadBtn = styled.button`
  justify-self: center;
  align-self: center;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #3b47ff;
  height: 25px;
  width: 35px;
  border-radius: 20px;
  margin-right: 8px;

  &:hover {
    cursor: ${(p) => (p.disabled ? `default` : `pointer`)};
    opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  }

  &:active {
    cursor: default;
    opacity: 1;
  }
`;
const ChipWrap = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;

  cursor: pointer;
  background: transparent;

  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'JetBrains Mono', serif;
  user-select: none;

  :hover {
    opacity: 0.5;
    box-shadow: none;
  }

  :active {
    opacity: 1;
  }

  color: #545454;

  border: ${(p) =>
    p.active ? `1px solid #4f49f3` : `1px solid rgba(0, 0, 0, 0.2)`};
  box-shadow: 0 1px 4px 0 rgba(68, 72, 80, 0.08);

  border-radius: 2rem;
  padding: 0.85rem 1.5rem;
`;

function isNotOrderDownloadAvailable(metricsStart: string) {
  if (isLocalDev()) {
    return false;
  }
  return !(
    useFeatureBit(FeatureBit.DOWNLOAD_EXP_ORDERS) &&
    isAfterMinDate(new Date(metricsStart))
  );
}

function DownIcon() {
  return (
    <StaticImage
      height={15}
      loading="eager"
      placeholder="none"
      src="../../assets/download.png"
      alt="download"
    />
  );
}

async function downloadFile(
  downloadUrl: string,
  fileName: string,
  expId: string,
  setLoading: (value: ((prevState: boolean) => boolean) | boolean) => void,
) {
  if (downloadUrl) {
    try {
      const response = await fetch(downloadUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement(`a`);
      a.href = url;
      a.setAttribute(`download`, `${fileName}_${expId}.csv`);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.log(`failed downloading csv report`, e);
    } finally {
      setLoading(false);
    }
  }
}

function isAfterMinDate(date: Date) {
  if (isLocalDev()) {
    return true;
  }
  return date.valueOf() >= new Date(`2024-06-23`).valueOf();
}

function isLocalDev() {
  return (
    window?.location?.hostname ===
      `pre-prod--kind-noether-230989.netlify.app` ||
    window?.location?.hostname === `vsly.local`
  );
}

function getCurrentVersion(versions: Array<ExpVersion>) {
  return versions?.reduce((p, c) => (p < c?.version ? c?.version : p), 1);
}

const SmallSwitch = styled(Switch)`
  && {
    transform: scale(0.8);
    cursor: grab;
  }
`;

const Col = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
`;

const FlexRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
